/**
 * Put text on the clipboard, or say plainly that it could not.
 *
 * Shared by every copy button, because each of the two failure modes below was
 * found once and must not be found again per button.
 */
export async function copyText(text: string): Promise<boolean> {
  try {
    // ⚠️ Raced against a timeout on purpose. `navigator.clipboard.writeText`
    // does not REJECT when the document lacks focus, it simply never settles,
    // and an await that never returns leaves the button showing nothing at
    // all, which in a room reads as "I clicked and it is broken".
    await Promise.race([
      navigator.clipboard.writeText(text),
      new Promise((_, reject) => window.setTimeout(reject, 1200)),
    ]);
    return true;
  } catch {
    /* fall through to the legacy path */
  }
  try {
    // The legacy path, for an insecure context or an older phone. A textarea
    // rather than an input, because an input would flatten the line breaks.
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.setAttribute("readonly", "");
    ta.style.position = "fixed";
    ta.style.opacity = "0";
    // iOS zooms the page to an input under 16px, and selects nothing in one
    // that is off screen.
    ta.style.fontSize = "16px";
    ta.style.top = "0";
    document.body.appendChild(ta);
    ta.select();
    ta.setSelectionRange(0, text.length);
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return ok;
  } catch {
    return false;
  }
}
